import React, { useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import useErrorHandler, {
  ApplicationError
} from "../../../../services/error-handler/ErrorHandlerService";
import { ProjectUsersStorage } from "../../../../services/user/ProjectUsersStorage";
import { UsersSummaryStorage } from "../../../../services/user/UsersSummaryStorage";
import { api } from "../../../../services/api/ApiService";
import { CurrentProjectStorageModel } from "../../../../services/project/CurrentProjectStorage";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { Dialog } from "../../../../components/dialog/Dialog";
import { UserRolesStorageModel } from "../../../../services/user/UserRolesStorage";
import { translationService } from "../../../../services/translation/TranslationService";
import DialogGrid from "../../../../components/dialog-grid/DialogGrid";

interface AddExistingUsersModalProps {
  isVisible: boolean;
  toggleModal: () => void;
  projectUsersStorage: ProjectUsersStorage;
}

export const AddExistingUsersModal = observer((props: AddExistingUsersModalProps) => {
  const { isVisible, toggleModal, projectUsersStorage } = props;

  const { handleErrorInSnackbar } = useErrorHandler();

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: projectUsersStorage.projectId }, { api })
  );

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [usersSummaryStorage] = useState(() =>
    UsersSummaryStorage.create({ useGridFiltering: true, autoLoad: false }, { api })
  );

  useEffect(() => {
    if (userRolesStorage.isLoading || !isVisible) {
      return;
    }

    usersSummaryStorage.setUserRoleOptions(toJS(userRolesStorage.userRoles));

    async function setUsersSummaryStorageFilter() {
      if (currentProjectStorage.currentProject && currentProjectStorage.currentProject.id > 0) {
        await usersSummaryStorage.setContextFilter({
          ProjectId: currentProjectStorage.currentProject.id,
          InProject: false,
        });
      }
    }

    setUsersSummaryStorageFilter();
  }, [
    currentProjectStorage.currentProject,
    isVisible,
    userRolesStorage.isLoading,
    userRolesStorage.userRoles,
    usersSummaryStorage,
  ]);

  const onCloseModal = useCallback(() => {
    usersSummaryStorage.resetDataQuery();
    usersSummaryStorage.resetGridFilter();
    toggleModal();
  }, [toggleModal, usersSummaryStorage]);

  const addUsersToCurrentProject = useCallback(async () => {
    try {
      await projectUsersStorage.addUsersToProject(
        currentProjectStorage.currentProject.id,
        usersSummaryStorage.selectedItems
      );
      snackbarNotificationsService.showSuccess("projects.AddExistingUsersModal.success", {
        usersCount: usersSummaryStorage.selectedItemsCount,
      });
      usersSummaryStorage.resetGridFilter();
    } catch (error) {
      handleErrorInSnackbar(error as ApplicationError);
    }
    toggleModal();
  }, [
    currentProjectStorage.currentProject.id,
    handleErrorInSnackbar,
    projectUsersStorage,
    toggleModal,
    usersSummaryStorage,
  ]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={onCloseModal}
      titleKey="projects.AddExistingUsersModal.label"
      buttonOk={{
        id: "button-add-user-ok",
        textKey: "projects.AddExistingUsersButton.ok",
        action: addUsersToCurrentProject,
        disabled: !usersSummaryStorage.anyItemsSelected,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: toggleModal,
      }}
    >
      <h3>{translationService.translate("projects.AddExistingUsersButton.instruction")}</h3>
      <DialogGrid itemTypeKey="general.users" dataGridStorage={usersSummaryStorage} />
    </Dialog>
  );
});
